import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { Subject, takeUntil, finalize, catchError, of } from 'rxjs';

import { FormBuilderService, FormBuilderState } from '../../../service/form-builder.service';
import { CanvasFieldComponent } from './canvas-field/canvas-field.component';
import { FormFieldModel, FieldType } from '../../../models/form-field.model';
import { PaletteField } from '../field-palette/field-palette.component';
import { MarketingFormsService } from '../../../service/marketing-forms.service';
import { extractApiErrorMessage } from '../../../utils/api-error.util';
import { BusinessApiService } from '../../../../businesses/services/business-api.service';
import { BusinessResponse } from '../../../../businesses/models/business.model';
import { ToastService } from '../../../../../shared/services/toast.service';
import { environment } from '../../../../../../environments/environment';

export interface PaletteDropEvent {
  type: FieldType;
  atIndex: number;
}

@Component({
  selector: 'app-form-canvas',
  standalone: true,
  imports: [CommonModule, DragDropModule, CanvasFieldComponent],
  templateUrl: './form-canvas.component.html',
  styles: [`
    :host ::ng-deep app-canvas-field.cdk-drag-placeholder {
      display: block;
      position: relative;
      min-height: 56px;
      border-radius: 12px;
      box-sizing: border-box;
    }
    :host ::ng-deep app-canvas-field.cdk-drag-placeholder * {
      visibility: hidden;
    }
    :host ::ng-deep app-canvas-field.cdk-drag-placeholder::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 12px;
      border: 2px dashed #93c5fd;
      background: #eff6ff;
      pointer-events: none;
    }
    :host ::ng-deep app-canvas-field.cdk-drag-animating {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
  `],
})
export class FormCanvasComponent implements OnInit, OnDestroy {
  @Output() fieldDroppedFromPalette = new EventEmitter<PaletteDropEvent>();
  @Output() removeFieldAt = new EventEmitter<string>();

  private readonly destroy$ = new Subject<void>();

  state: FormBuilderState = {
    form: null, fields: [], settings: null,
    selectedFieldKey: null, activeStep: 1, isDirty: false, isLoading: false
  };

  logoUrl: string | null = null;
  logoUploading = false;
  private currentBusiness: BusinessResponse | null = null;
  private readonly filesApiUrl = `${environment.apiUrl}api/files`;

  get fields(): FormFieldModel[] {
    const isMultiStep = !!this.state?.settings?.isMultiStep;
    const activeStep = this.state?.activeStep ?? 1;
    if (!isMultiStep) return this.state?.fields ?? [];
    return this.state?.fields?.filter(f => (f.stepNumber ?? 1) === activeStep) ?? [];
  }

  get stepsArray(): number[] {
    const total = this.state?.settings?.totalSteps ?? 1;
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  private readonly formsService = inject(MarketingFormsService);
  private readonly businessApi = inject(BusinessApiService);
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);

  constructor(readonly builder: FormBuilderService) {}

  ngOnInit(): void {
    this.builder.state.pipe(takeUntil(this.destroy$)).subscribe(s => (this.state = s));
    this.loadBusinessLogo();
  }

  private loadBusinessLogo(): void {
    this.businessApi.getAll(0, 1).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (page) => {
        const biz = page?.content?.[0];
        if (biz) {
          this.currentBusiness = biz;
          this.logoUrl = biz.logoUrl ?? null;
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDrop(event: CdkDragDrop<any>): void {
    if (event.previousContainer === event.container) {
      // Reordena solo dentro de la lista renderizada (paso activo en multi-paso).
      const renderedKeys = this.fields.map(f => f.fieldKey);
      this.builder.reorderFields(event.previousIndex, event.currentIndex, renderedKeys);
    } else {
      const palette = event.item.data as PaletteField;
      this.fieldDroppedFromPalette.emit({ type: palette.type, atIndex: event.currentIndex });
    }
  }

  onSelectField(fieldKey: string): void {
    if (this.state.selectedFieldKey === fieldKey) {
      this.builder.deselectField();
    } else {
      this.builder.selectField(fieldKey);
    }
  }

  onRemoveField(fieldKey: string): void {
    this.removeFieldAt.emit(fieldKey);
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.logoUploading = true;
    const formData = new FormData();
    formData.append('file', file);

    this.http.post<{ publicUrl: string }>(
      `${this.filesApiUrl}/upload?category=COMPANY_LOGO&isPublic=true`,
      formData
    ).pipe(
      finalize(() => { this.logoUploading = false; input.value = ''; }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (res) => {
        this.logoUrl = res.publicUrl;
        // inc36: endpoint dedicado PATCH /businesses/{id}/logo (permiso forms:EDIT),
        // no manda name ni toca isActive.
        this.persistLogo(res.publicUrl);
      },
      error: err => this.toast.error(extractApiErrorMessage(err, 'No se pudo subir el logo')),
    });
  }

  // inc36: guarda el logo en el negocio con el endpoint dedicado. Si el negocio aún
  // no se cargó, lo trae primero para conocer su id.
  private persistLogo(logoUrl: string | null): void {
    const apply = (id: number) => {
      this.businessApi.updateLogo(id, logoUrl).pipe(takeUntil(this.destroy$)).subscribe({
        next: (biz) => {
          this.currentBusiness = biz;
          this.toast.success('Logo actualizado');
        },
        error: err => this.toast.error(extractApiErrorMessage(err, 'El logo se subió pero no se pudo guardar en el negocio')),
      });
    };

    if (this.currentBusiness) {
      apply(this.currentBusiness.id);
      return;
    }
    this.businessApi.getAll(0, 1).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => {
        const biz = page?.content?.[0];
        if (!biz) {
          this.toast.error('El logo se subió pero no se pudo identificar el negocio');
          return;
        }
        this.currentBusiness = biz;
        apply(biz.id);
      },
      error: err => this.toast.error(extractApiErrorMessage(err, 'El logo se subió pero no se pudo guardar en el negocio')),
    });
  }

  onLogoLoadError(): void {
    // Image URL is set but the browser couldn't render it — fall back to the upload button
    this.logoUrl = null;
  }

  // inc25: el título grande del constructor edita el TÍTULO VISIBLE (title),
  // no el nombre interno (name). El nombre interno se edita en la cabecera / detalle.
  onTitleBlur(event: Event): void {
    const title = (event.target as HTMLInputElement).value.trim();
    const formId = this.state?.form?.id;
    if (!formId || title === (this.state.form?.title ?? '')) return;
    this.builder.syncFormMeta({ title });
    this.formsService.updateForm(formId, { title }).pipe(takeUntil(this.destroy$)).subscribe();
  }

  get submitLabel(): string {
    return this.state?.settings?.submitButtonLabel?.trim() || 'Enviar';
  }

  get formName(): string {
    return this.state.form?.name || '';
  }

  // inc25: título visible del formulario (cae al nombre solo como referencia vacía).
  get formTitle(): string {
    return this.state.form?.title || '';
  }

  get formDescription(): string {
    return this.state.form?.description || '';
  }

  get submitButtonPreviewStyle() {
    const raw = this.state?.settings?.styleConfig;
    if (!raw) return {};
    try {
      const s = JSON.parse(raw) as Record<string, any>;
      return {
        backgroundColor: s['primaryColor'] || null,
        color: s['buttonTextColor'] || null,
        borderRadius: s['borderRadius'] != null ? `${s['borderRadius']}px` : null,
      };
    } catch {
      return {};
    }
  }

  onDescriptionBlur(event: Event): void {
    const description = (event.target as HTMLTextAreaElement).value;
    const formId = this.state?.form?.id;
    if (formId == null || description === (this.state.form?.description ?? '')) return;
    this.builder.syncFormMeta({ description });
    this.formsService.updateForm(formId, { description }).pipe(takeUntil(this.destroy$)).subscribe();
  }
}
